import React, { PropsWithChildren, useState } from 'react';

import { ListingFilterItemInterface } from '@components/ui/filters/MultiSelectionFilter/types';
import { HOTEL_SORT_DATA } from '@constants/constants';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import { createGenericContext } from './generateContext';

type FilterItem = ListingFilterItemInterface;
export interface FilterKind {
  items: FilterItem[];
  type: string;
}

export interface AvailableFilterKinds {
  regions: Array<FilterItem>;
  selectedRegions: React.Key[];
  freeCancellation: boolean;
  onlyAvailableProperties: boolean;
  hotelName: string;
  keywords: string;
  minAccommodationPrice: number;
  maxAccommodationPrice: number;
  selectedThemeItems: FilterItem[];
  selectedCategoryItems: FilterItem[];
  selectedFeaturesItems: FilterItem[];
  selectedPopularitiesItems: FilterItem[];
  selectedAccommodationTypes: FilterItem[];
  selectedDateItems: FilterItem[];
  selectedDepartureItems: FilterItem[];
  selectedTransportationItems: FilterItem[];
  selectedVisaItems: FilterItem[];
  selectedSpecialPeriodItems: FilterItem[];
  selectedTripAdvisorItem: string;
  selectedStarItems?: FilterItem[];
}

export interface SortInterface {
  id: string;
  label: string;
  disabled: boolean;
}

export type FilterContextType = {
  filters: Partial<AvailableFilterKinds>;
  setFilters: React.Dispatch<
    React.SetStateAction<Partial<AvailableFilterKinds>>
  >;
  categoryItems: FilterItem[];
  setCategoryItems: React.Dispatch<React.SetStateAction<FilterItem[]>>;
  sort: SortInterface;
  setSort: React.Dispatch<React.SetStateAction<SortInterface>>;
};

const [useFiltersContext, FiltersContextProvider] =
  createGenericContext<FilterContextType>();

const FiltersProvider = (props: PropsWithChildren) => {
  const query = useSearchParamsToQuery();

  const [filters, setFilters] = useState<Partial<AvailableFilterKinds>>({
    regions: undefined,
    selectedRegions: [],
    freeCancellation: false,
    onlyAvailableProperties: false,
    hotelName: '',
    keywords: '',
    minAccommodationPrice: 0,
    maxAccommodationPrice: 0,
    selectedThemeItems: [],
    selectedCategoryItems: [],
    selectedFeaturesItems: [],
    selectedPopularitiesItems: [],
    selectedAccommodationTypes: [],
    selectedDateItems: [],
    selectedDepartureItems: [],
    selectedTransportationItems: [],
    selectedVisaItems: [],
    selectedSpecialPeriodItems: [],
    selectedTripAdvisorItem: '',
    selectedStarItems: [],
  });

  const [categoryItems, setCategoryItems] = useState<FilterItem[]>([]);

  const { ['sort']: sortQuery } = query;
  const initialSort =
    HOTEL_SORT_DATA.find((d) => d.id === sortQuery) || HOTEL_SORT_DATA[0];

  const [sort, setSort] = useState<SortInterface>(initialSort);

  return (
    <FiltersContextProvider
      value={{
        filters,
        setFilters,
        categoryItems,
        setCategoryItems,
        sort,
        setSort,
      }}
      {...props}
    />
  );
};
export { useFiltersContext, FiltersProvider };
